import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import {
  Badge,
  Box,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Icon,
  IconPicker,
  ScrollArea,
  Spinner,
  Text,
  toastManager,
  useIsMobile,
  type Color,
  type IconName,
} from "@salestrak/ui";

import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useWorkflowDetail } from "#app/lib/hooks/use-workflow-detail";
import { PageHeader, PageHeaderBack, PageHeaderTitle } from "#app/components/page-header";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import { useAppData } from "#app/lib/data";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";
import { EditableField } from "#app/components/settings/editable-field";
import { DeleteWorkflowDialog } from "#app/components/workflows/delete-workflow-dialog";
import { withPermission } from "#app/components/permissions/require-permission";
import { EntityTag } from "#app/components/entity";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";

function WorkflowEditPage() {
  const isMobile = useIsMobile();
  const { workflowId } = Route.useParams();
  const { push, replace } = useAppNavigate();
  const { refetch: refetchAppData } = useAppData();
  const { updateWorkflow, setDefaultWorkflow, validateWorkflowKey } =
    useWorkflowMutations();

  const { workflow, stageCount, loading, error } = useWorkflowDetail(workflowId);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [keyError, setKeyError] = useState("");

  const handleFieldSave = useCallback(
    async (fieldId: string, value: string) => {
      if (!workflowId) return;

      if (fieldId === "key") {
        const upper = value
          .replace(/[^a-zA-Z0-9]/g, "")
          .slice(0, 6)
          .toUpperCase();
        if (upper.length >= 3 && upper !== workflow?.key) {
          try {
            const available = await validateWorkflowKey(upper);
            if (!available) {
              setKeyError("Key already in use");
              return;
            }
            setKeyError("");
          } catch {
            return;
          }
        } else {
          setKeyError("");
        }
        await updateWorkflow(workflowId, { key: upper || null });
      } else {
        await updateWorkflow(workflowId, { [fieldId]: value });
        if (fieldId === "name") refetchAppData();
      }
    },
    [
      workflowId,
      updateWorkflow,
      validateWorkflowKey,
      workflow?.key,
      refetchAppData,
    ],
  );

  const workflowIcon = workflow?.icon.icon;
  const workflowColor = workflow?.icon.color;

  const handleIconChange = useCallback(
    async (icon: IconName) => {
      if (!workflowId || !workflowColor) return;
      await updateWorkflow(workflowId, {
        icon: { icon, color: workflowColor },
      });
      refetchAppData();
    },
    [workflowId, workflowColor, updateWorkflow, refetchAppData],
  );

  const handleColorChange = useCallback(
    async (color: Color) => {
      if (!workflowId || !workflowIcon) return;
      await updateWorkflow(workflowId, {
        icon: { icon: workflowIcon, color },
      });
      refetchAppData();
    },
    [workflowId, workflowIcon, updateWorkflow, refetchAppData],
  );

  const handleSetDefault = useCallback(async () => {
    if (!workflowId) return;
    try {
      await setDefaultWorkflow(workflowId);
      toastManager.add({ title: "Workflow set as default", type: "success" });
    } catch (err) {
      toastMutationError(err, "Failed to set default workflow");
    }
  }, [workflowId, setDefaultWorkflow]);

  const handleDeleted = useCallback(() => {
    setDeleteOpen(false);
    refetchAppData();
    replace("/settings/workflows");
  }, [replace, refetchAppData]);

  if (loading && !workflow) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (!workflow) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load workflow</EmptyTitle>
            {error ? (
              <EmptyDescription>{error.message}</EmptyDescription>
            ) : null}
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="Workflows" onClick={() => replace("/settings/workflows")} />
          <PageHeaderTitle
            adornment={
              workflow.isDefault ? (
                <Badge size="sm">
                  <Text>Default</Text>
                </Badge>
              ) : null
            }
          >
            {workflow.name}
          </PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>
            {/* Name & icon / Identifier */}
            <SettingsGroup label="General">
              <SettingsGroupRow label="Name & Icon">
                <Box direction="row" gap="sm" align="center" fill={isMobile ? "width" : undefined}>
                  <IconPicker
                    icon={(workflow.icon.icon ?? ENTITY_KIND_ICON.workflow) as IconName}
                    color={(workflow.icon.color ?? "blue") as Color}
                    onIconChange={handleIconChange}
                    onColorChange={handleColorChange}
                    size="sm"
                  />
                  <Box fill={isMobile ? "flex" : undefined}>
                    <EditableField
                      fieldId="name"
                      label="Workflow name"
                      initialValue={workflow.name ?? ""}
                      onSave={handleFieldSave}
                      placeholder="Workflow name"
                      width={isMobile ? "full" : "xl"}
                    />
                  </Box>
                </Box>
              </SettingsGroupRow>
              <SettingsGroupRow
                label="Identifier"
                subtitle="Unique key for this workflow"
              >
                <Box gap="xs" width={isMobile ? undefined : 232} fill={isMobile ? "width" : undefined}>
                  <EditableField
                    fieldId="key"
                    label="Workflow key"
                    initialValue={workflow.key ?? ""}
                    onSave={handleFieldSave}
                    placeholder="e.g. PRD"
                    width="full"
                  />
                  {keyError ? (
                    <Text size="xs" variant="destructive">
                      {keyError}
                    </Text>
                  ) : null}
                </Box>
              </SettingsGroupRow>
              <SettingsGroupRow
                label="Set workflow as default"
                subtitle="Default to this workflow when creating a new project."
              >
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSetDefault}
                  disabled={workflow.isDefault}
                >
                  <Text>Set as default</Text>
                </Button>
              </SettingsGroupRow>
            </SettingsGroup>

            {/* Stages */}
            <SettingsGroup>
              <SettingsGroupRow
                label="Stages"
                subtitle="Customize and manage stages for the workflow"
                leading={
                  <EntityTag
                    entity={{
                      kind: "stage",
                      // Decorative glyph for the stages row — not a real stage.
                      id: "stages-row-glyph",
                      stage: {
                        type: "inProgress",
                        icon: { color: "teal", percentage: 50 },
                      },
                    }}
                    display="visual"
                    size="md"
                    interactive={false}
                  />
                }
                onClick={() => push(`/settings/workflows/${workflowId}/stages`)}
                actionsVisible
              >
                <Box direction="row" gap="sm" align="center">
                  <Text size="sm" variant="muted">
                    {stageCount} stages
                  </Text>
                  <Icon name="chevron-right" color="muted" size="sm" />
                </Box>
              </SettingsGroupRow>
            </SettingsGroup>

            {/* Danger Zone */}
            <SettingsGroup label="Danger zone">
              <SettingsGroupRow
                label="Delete workflow"
                subtitle="Permanently remove this workflow and its stages."
              >
                <Button
                  variant="ghost"
                  className="text-destructive-foreground"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                  disabled={workflow.isDefault}
                >
                  <Text>Delete workflow...</Text>
                </Button>
              </SettingsGroupRow>
            </SettingsGroup>
          </Box>
        </Box>

        <DeleteWorkflowDialog
          workflow={deleteOpen ? workflow : null}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onDeleted={handleDeleted}
          onManageWorkflow={(id) => replace(`/workflow/${id}`)}
        />
      </ScrollArea>
    </Box>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/workflows/$workflowId/",
)({ component: withPermission("manage.edit_workflows", WorkflowEditPage) });
